import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, Scale, Briefcase, CheckCircle2, Phone, ArrowRight, X, ExternalLink, ChevronRight, AlertOctagon, ClipboardList, Banknote, LogOut, ArrowRightLeft, Car, IdCard, ShieldAlert, Gavel, Search, Landmark, Unlock, Send, FileCheck, MessageCircle } from 'lucide-react';
const FadeIn = ({
  children,
  delay = 0
}: {
  children: React.ReactNode;
  delay?: number;
}) => <motion.div initial={{
  opacity: 0,
  y: 20
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-100px'
}} transition={{
  duration: 0.7,
  delay,
  ease: [0.21, 0.47, 0.32, 0.98]
}} data-id="element-0">
    {children}
  </motion.div>;
export function App() {
  const [showPitchPopup, setShowPitchPopup] = useState(false);
  const [hasSeenPopup, setHasSeenPopup] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      if (hasSeenPopup) return;
      const scrollPosition = window.scrollY;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollPercentage = scrollPosition / (documentHeight - windowHeight) * 100;
      if (scrollPercentage >= 50) {
        setShowPitchPopup(true);
        setHasSeenPopup(true);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [hasSeenPopup]);
  const phoneNumber = '+38 050 645 06 23';
  const phoneLink = 'tel:+380506450623';
  const telegramLink = 'https://t.me/+380506450623';
  const viberLink = 'viber://chat?number=%2B380506450623';
  return <div className="min-h-screen bg-[#FDFCFB] text-zinc-900 font-sans selection:bg-zinc-200 selection:text-zinc-900" data-id="element-1">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-40 bg-[#FDFCFB]/90 backdrop-blur-md border-b border-zinc-200/60" data-id="element-2">
        <div className="max-w-6xl mx-auto px-6 h-20 flex items-center justify-between" data-id="element-3">
          <div className="flex items-center gap-3" data-id="element-4">
            <div className="w-8 h-8 bg-zinc-900 text-white flex items-center justify-center rounded-lg" data-id="element-5">
              <Scale className="w-4 h-4" data-id="element-6" />
            </div>
            <span className="font-semibold text-lg tracking-tight" data-id="element-7">
              Адвокатське Бюро
            </span>
          </div>
          <a href={phoneLink} className="hidden md:flex items-center gap-2 text-sm font-medium text-zinc-600 hover:text-zinc-900 transition-colors" data-id="element-8">
            <Phone className="w-4 h-4" data-id="element-9" />
            {phoneNumber}
          </a>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-40 pb-24 px-6" data-id="element-10">
        <div className="max-w-4xl mx-auto text-center" data-id="element-11">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          ease: [0.21, 0.47, 0.32, 0.98]
        }} data-id="element-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-zinc-200 bg-white text-zinc-600 text-sm font-medium mb-8 shadow-sm" data-id="element-13">
              Працюємо по всій Україні
            </div>
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight text-zinc-900 mb-8 leading-[1.1]" data-id="element-14">
              Професійний <br className="hidden md:block" data-id="element-15" />
              юридичний захист
            </h1>
            <p className="text-xl text-zinc-600 mb-12 max-w-2xl mx-auto leading-relaxed" data-id="element-16">
              Спеціалізація на військовому праві (СЗЧ, ВЛК, виплати) та
              адміністративних справах (Ст. 130 КУпАП). Надійний супровід на
              кожному етапі вашої справи.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-17">
              <a href={phoneLink} className="w-full sm:w-auto px-8 py-4 bg-zinc-900 text-white rounded-xl font-medium hover:bg-zinc-800 transition-all active:scale-95 flex items-center justify-center gap-2" data-id="element-18">
                <Phone className="w-5 h-5" data-id="element-19" />
                Зателефонувати
              </a>
              <a href="#services" className="w-full sm:w-auto px-8 py-4 bg-white text-zinc-900 rounded-xl font-medium hover:bg-zinc-50 transition-all active:scale-95 flex items-center justify-center gap-2 border border-zinc-200 shadow-sm" data-id="element-20">
                Наші послуги
                <ArrowRight className="w-5 h-5 text-zinc-400" data-id="element-21" />
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 px-6 bg-white border-y border-zinc-200/60" data-id="element-22">
        <div className="max-w-6xl mx-auto" data-id="element-23">
          <FadeIn data-id="element-24">
            <div className="mb-20 md:text-center" data-id="element-25">
              <h2 className="text-4xl md:text-5xl font-bold mb-6 tracking-tight text-zinc-900" data-id="element-26">
                Спеціалізація бюро
              </h2>
              <p className="text-zinc-600 max-w-3xl mx-auto text-lg leading-relaxed" data-id="element-27">
                Ми надаємо фахову правову допомогу у найскладніших категоріях
                справ. Наша експертиза дозволяє знаходити вихід там, де інші
                бачать глухий кут.
              </p>
            </div>
          </FadeIn>

          <div className="space-y-24" data-id="element-28">
            {/* Category 1: Military Law */}
            <div data-id="element-29">
              <FadeIn data-id="element-30">
                <div className="flex items-center gap-4 mb-10 border-b border-zinc-200 pb-6" data-id="element-31">
                  <div className="w-14 h-14 rounded-xl bg-zinc-100 flex items-center justify-center" data-id="element-32">
                    <Shield className="w-7 h-7 text-zinc-800" data-id="element-33" />
                  </div>
                  <div data-id="element-34">
                    <h3 className="text-3xl font-bold text-zinc-900" data-id="element-35">
                      Військове право
                    </h3>
                    <p className="text-zinc-500 mt-1 text-lg" data-id="element-36">
                      Комплексний захист прав військовослужбовців
                    </p>
                  </div>
                </div>
              </FadeIn>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-37">
                {[{
                icon: AlertOctagon,
                title: 'СЗЧ та Дезертирство',
                desc: "Захист у справах про самовільне залишення частини (ст. 407 ККУ) та дезертирство (ст. 408 ККУ). Пом'якшення відповідальності, повернення на службу."
              }, {
                icon: ClipboardList,
                title: 'Оскарження рішень ВЛК',
                desc: 'Юридичний супровід проходження військово-лікарської комісії. Оскарження незаконних висновків ВЛК до ЦВЛК або суду.'
              }, {
                icon: Banknote,
                title: 'Бойові виплати та ОГД',
                desc: 'Стягнення заборгованості по бойовим виплатам (100 тис., 30 тис.), отримання одноразової грошової допомоги при пораненні чи загибелі.'
              }, {
                icon: LogOut,
                title: 'Звільнення зі служби',
                desc: "Законне звільнення з лав ЗСУ за станом здоров'я, сімейними обставинами або у зв'язку з досягненням граничного віку."
              }, {
                icon: ArrowRightLeft,
                title: 'Переведення до іншої частини',
                desc: "Допомога в оформленні рапортів та супровід процесу переміщення військовослужбовця за станом здоров'я або відношенням."
              }, {
                icon: FileCheck,
                title: 'Отримання статусу УБД',
                desc: 'Збір документів та юридичний супровід процедури отримання статусу учасника бойових дій.'
              }].map((item, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-38">
                    <div className="bg-[#FDFCFB] p-8 rounded-2xl border border-zinc-200 hover:border-zinc-400 transition-colors h-full" data-id="element-39">
                      <div className="w-12 h-12 rounded-lg bg-white border border-zinc-200 flex items-center justify-center mb-6 shadow-sm" data-id="element-40">
                        <item.icon className="w-6 h-6 text-zinc-700" data-id="element-41" />
                      </div>
                      <h4 className="text-xl font-bold mb-3 text-zinc-900" data-id="element-42">
                        {item.title}
                      </h4>
                      <p className="text-zinc-600 leading-relaxed" data-id="element-43">
                        {item.desc}
                      </p>
                    </div>
                  </FadeIn>)}
              </div>
            </div>

            {/* Category 2: Article 130 */}
            <div data-id="element-44">
              <FadeIn data-id="element-45">
                <div className="flex items-center gap-4 mb-10 border-b border-zinc-200 pb-6" data-id="element-46">
                  <div className="w-14 h-14 rounded-xl bg-zinc-100 flex items-center justify-center" data-id="element-47">
                    <Car className="w-7 h-7 text-zinc-800" data-id="element-48" />
                  </div>
                  <div data-id="element-49">
                    <h3 className="text-3xl font-bold text-zinc-900" data-id="element-50">
                      Ст. 130 КУпАП
                    </h3>
                    <p className="text-zinc-500 mt-1 text-lg" data-id="element-51">
                      Захист водіїв від незаконних звинувачень
                    </p>
                  </div>
                </div>
              </FadeIn>
              <div className="grid md:grid-cols-3 gap-6" data-id="element-52">
                {[{
                icon: IdCard,
                title: 'Повернення водійських прав',
                desc: 'Представництво в суді з метою недопущення позбавлення права керування транспортними засобами на 1 рік і більше.'
              }, {
                icon: ShieldAlert,
                title: 'Оскарження дій поліції',
                desc: 'Виявлення процесуальних порушень при складанні протоколу, незаконної зупинки або порушень процедури освідування.'
              }, {
                icon: Gavel,
                title: 'Закриття справ',
                desc: 'Доведення відсутності складу правопорушення, закриття справи за строками давності або через недопустимість доказів.'
              }].map((item, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-53">
                    <div className="bg-[#FDFCFB] p-8 rounded-2xl border border-zinc-200 hover:border-zinc-400 transition-colors h-full" data-id="element-54">
                      <div className="w-12 h-12 rounded-lg bg-white border border-zinc-200 flex items-center justify-center mb-6 shadow-sm" data-id="element-55">
                        <item.icon className="w-6 h-6 text-zinc-700" data-id="element-56" />
                      </div>
                      <h4 className="text-xl font-bold mb-3 text-zinc-900" data-id="element-57">
                        {item.title}
                      </h4>
                      <p className="text-zinc-600 leading-relaxed" data-id="element-58">
                        {item.desc}
                      </p>
                    </div>
                  </FadeIn>)}
              </div>
            </div>

            {/* Category 3: Criminal Defense */}
            <div data-id="element-59">
              <FadeIn data-id="element-60">
                <div className="flex items-center gap-4 mb-10 border-b border-zinc-200 pb-6" data-id="element-61">
                  <div className="w-14 h-14 rounded-xl bg-zinc-100 flex items-center justify-center" data-id="element-62">
                    <Scale className="w-7 h-7 text-zinc-800" data-id="element-63" />
                  </div>
                  <div data-id="element-64">
                    <h3 className="text-3xl font-bold text-zinc-900" data-id="element-65">
                      Кримінальний захист
                    </h3>
                    <p className="text-zinc-500 mt-1 text-lg" data-id="element-66">
                      Захист підозрюваних та обвинувачених
                    </p>
                  </div>
                </div>
              </FadeIn>
              <div className="grid md:grid-cols-3 gap-6" data-id="element-67">
                {[{
                icon: Search,
                title: 'Супровід на допитах',
                desc: 'Участь адвоката у слідчих діях (допити, обшуки, слідчі експерименти) для недопущення тиску та порушення прав.'
              }, {
                icon: Landmark,
                title: 'Захист у суді',
                desc: 'Побудова ефективної стратегії захисту, збір доказів невинуватості та представництво в судах всіх інстанцій.'
              }, {
                icon: Unlock,
                title: 'Зміна запобіжного заходу',
                desc: "Оскарження тримання під вартою, заміна на більш м'який запобіжний захід (застава, домашній арешт, порука)."
              }].map((item, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-68">
                    <div className="bg-[#FDFCFB] p-8 rounded-2xl border border-zinc-200 hover:border-zinc-400 transition-colors h-full" data-id="element-69">
                      <div className="w-12 h-12 rounded-lg bg-white border border-zinc-200 flex items-center justify-center mb-6 shadow-sm" data-id="element-70">
                        <item.icon className="w-6 h-6 text-zinc-700" data-id="element-71" />
                      </div>
                      <h4 className="text-xl font-bold mb-3 text-zinc-900" data-id="element-72">
                        {item.title}
                      </h4>
                      <p className="text-zinc-600 leading-relaxed" data-id="element-73">
                        {item.desc}
                      </p>
                    </div>
                  </FadeIn>)}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits & Contact */}
      <section className="py-24 px-6 bg-[#FDFCFB]" data-id="element-74">
        <div className="max-w-6xl mx-auto" data-id="element-75">
          <div className="grid md:grid-cols-2 gap-16 items-center" data-id="element-76">
            <FadeIn data-id="element-77">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-zinc-900" data-id="element-78">
                Чому клієнти довіряють нам свої справи?
              </h2>
              <p className="text-lg text-zinc-600 mb-8" data-id="element-79">
                Ми не даємо порожніх обіцянок. Наша мета — реальний результат та
                захист ваших законних прав.
              </p>
              <div className="space-y-6" data-id="element-80">
                {['Багаторічний досвід у вузькопрофільних справах', 'Повна конфіденційність та захист даних', 'Прозоре ціноутворення без прихованих платежів', 'Оперативність у прийнятті рішень'].map((benefit, i) => <div key={i} className="flex items-start gap-4" data-id="element-81">
                    <div className="mt-1" data-id="element-82">
                      <CheckCircle2 className="w-6 h-6 text-zinc-800" data-id="element-83" />
                    </div>
                    <span className="text-zinc-700 font-medium text-lg" data-id="element-84">
                      {benefit}
                    </span>
                  </div>)}
              </div>
            </FadeIn>
            <FadeIn delay={0.2} data-id="element-85">
              <div className="bg-white p-10 rounded-3xl border border-zinc-200 shadow-sm" data-id="element-86">
                <div className="text-center mb-8" data-id="element-87">
                  <div className="w-16 h-16 bg-zinc-100 rounded-2xl mx-auto mb-6 flex items-center justify-center" data-id="element-88">
                    <Briefcase className="w-8 h-8 text-zinc-800" data-id="element-89" />
                  </div>
                  <h3 className="text-2xl font-bold text-zinc-900" data-id="element-90">
                    Потрібна допомога?
                  </h3>
                  <p className="text-zinc-500 mt-2" data-id="element-91">
                    Отримайте первинну консультацію
                  </p>
                </div>

                <a href={phoneLink} className="w-full py-4 bg-zinc-900 text-white rounded-xl font-medium hover:bg-zinc-800 transition-all active:scale-95 flex items-center justify-center gap-2 mb-4" data-id="element-92">
                  <Phone className="w-5 h-5" data-id="element-93" />
                  {phoneNumber}
                </a>

                <div className="grid grid-cols-2 gap-4" data-id="element-94">
                  <a href={telegramLink} target="_blank" rel="noopener noreferrer" className="py-3 bg-[#229ED9]/10 text-[#229ED9] rounded-xl font-medium hover:bg-[#229ED9]/20 transition-colors flex items-center justify-center gap-2" data-id="element-95">
                    <Send className="w-5 h-5" data-id="element-96" />
                    Telegram
                  </a>
                  <a href={viberLink} className="py-3 bg-[#7360F2]/10 text-[#7360F2] rounded-xl font-medium hover:bg-[#7360F2]/20 transition-colors flex items-center justify-center gap-2" data-id="element-97">
                    <MessageCircle className="w-5 h-5" data-id="element-98" />
                    Viber
                  </a>
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-zinc-950 text-zinc-400 py-12 px-6" data-id="element-99">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6" data-id="element-100">
          <div className="flex items-center gap-3 text-white" data-id="element-101">
            <div className="w-8 h-8 bg-zinc-800 flex items-center justify-center rounded-lg" data-id="element-102">
              <Scale className="w-4 h-4" data-id="element-103" />
            </div>
            <span className="font-semibold text-lg tracking-tight" data-id="element-104">
              Адвокатське Бюро
            </span>
          </div>
          <div className="text-sm" data-id="element-105">
            © {new Date().getFullYear()} Всі права захищено.
          </div>
        </div>
      </footer>

      {/* Agency Pitch Popup */}
      <AnimatePresence data-id="element-106">
        {showPitchPopup && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 pointer-events-none" data-id="element-107">
            {/* Backdrop */}
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="absolute inset-0 bg-zinc-900/40 backdrop-blur-sm pointer-events-auto" onClick={() => setShowPitchPopup(false)} data-id="element-108" />

            {/* Modal */}
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 300
        }} className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden pointer-events-auto border border-zinc-200" data-id="element-109">
              <div className="p-8" data-id="element-110">
                <div className="flex justify-between items-start mb-6" data-id="element-111">
                  <div className="w-12 h-12 bg-zinc-100 rounded-xl flex items-center justify-center border border-zinc-200" data-id="element-112">
                    <Briefcase className="w-6 h-6 text-zinc-800" data-id="element-113" />
                  </div>
                  <button onClick={() => setShowPitchPopup(false)} className="p-2 text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 rounded-lg transition-colors" data-id="element-114">
                    <X className="w-5 h-5" data-id="element-115" />
                  </button>
                </div>

                <h3 className="text-2xl font-bold text-zinc-900 mb-3" data-id="element-116">
                  Сподобався цей сайт?
                </h3>
                <p className="text-zinc-600 leading-relaxed mb-8" data-id="element-117">
                  Ми створюємо преміальні сайти під ключ, адаптовані під ваші
                  бізнес-потреби. Вартість розробки — всього{' '}
                  <strong className="text-zinc-900 font-semibold" data-id="element-118">
                    8 000 грн
                  </strong>
                  .
                </p>

                <div className="space-y-3" data-id="element-119">
                  <a href="tel:+380508399309" className="flex items-center justify-between p-4 rounded-xl bg-zinc-50 hover:bg-zinc-100 border border-zinc-200 transition-colors group" data-id="element-120">
                    <div className="flex items-center gap-4" data-id="element-121">
                      <div className="w-10 h-10 rounded-lg bg-white border border-zinc-200 flex items-center justify-center text-zinc-700 group-hover:text-zinc-900 transition-colors" data-id="element-122">
                        <Phone className="w-5 h-5" data-id="element-123" />
                      </div>
                      <div data-id="element-124">
                        <div className="text-xs text-zinc-500 font-medium uppercase tracking-wider mb-0.5" data-id="element-125">
                          Зателефонувати нам
                        </div>
                        <div className="text-zinc-900 font-semibold" data-id="element-126">
                          +380 50 839 93 09
                        </div>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-zinc-400 group-hover:text-zinc-900 transition-colors" data-id="element-127" />
                  </a>

                  <a href="https://jetly.agency/ua" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between p-4 rounded-xl bg-zinc-900 hover:bg-zinc-800 text-white transition-colors group" data-id="element-128">
                    <div className="flex items-center gap-4" data-id="element-129">
                      <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center" data-id="element-130">
                        <ExternalLink className="w-5 h-5" data-id="element-131" />
                      </div>
                      <div data-id="element-132">
                        <div className="text-xs text-zinc-400 font-medium uppercase tracking-wider mb-0.5" data-id="element-133">
                          Наше агентство
                        </div>
                        <div className="font-semibold" data-id="element-134">jetly.agency/ua</div>
                      </div>
                    </div>
                    <ArrowRight className="w-5 h-5 text-zinc-400 group-hover:text-white transition-colors" data-id="element-135" />
                  </a>
                </div>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>
    </div>;
}